<template>
  <div>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      {{ router.currentRoute.value.params.groupId ? "Update" : "Add" }} Group
    </h2>
    <form @submit.prevent="submitForm">
      <Card class="mb-3">
        <template #title>
          <div class="text-xl">Group Information</div>
        </template>
        <template #content>
          <div class="grid">
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="groupName">Group Name</label>
              <InputText
                id="groupName"
                v-model="form.name"
                :class="{ 'p-invalid': touched.name && !form.name }"
                placeholder="e.g. My Group"
                @blur="touched.name = true"
              />
              <small v-if="touched.name && !form.name" class="p-error"
                >Name is required</small
              >
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="email">Email</label>
              <InputText
                id="email"
                v-model="form.email"
                :class="{
                  'p-invalid': touched.email && (!form.email || !emailValid),
                }"
                placeholder="e.g. group@example.com"
                type="email"
                autocomplete="new-email"
                @blur="handleEmailBlur"
              />
              <small v-if="touched.email && !form.email" class="p-error"
                >Email is required</small
              >
              <small
                v-else-if="touched.email && form.email && !emailValid"
                class="p-error"
                >Invalid email format</small
              >
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="password">Password</label>
              <InputText
                id="password"
                v-model="form.password"
                autocomplete="new-password"
                type="password"
                :class="{
                  'p-invalid': touched.password && !passwordValid,
                }"
                placeholder="Password"
                @blur="handlePasswordBlur"
              />
              <small v-if="touched.password && !passwordValid" class="p-error"
                >Password must be at least 8 chars, with upper-case and
                lower-case letters</small
              >
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="display_id">Display ID</label>
              <InputText id="display_id" v-model="form.display_id" readonly />
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="company_domain">Company Domain: </label>
              <InputText
                id="company_domain"
                v-model="form.company_domain"
                placeholder="Company Domain"
                :class="{
                  'p-invalid':
                    touched.company_domain &&
                    (!form.company_domain || !domainValid),
                }"
                @blur="handleDomainBlur"
              />
              <small
                v-if="touched.company_domain && !form.company_domain"
                class="p-error"
                >Company domain is required</small
              >
              <small
                v-else-if="
                  touched.company_domain && form.company_domain && !domainValid
                "
                class="p-error"
                >A valid domain is required</small
              >
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="seller_type">Seller Type:</label>
              <Dropdown
                id="seller_type"
                v-model="form.seller_type"
                :options="sellerTypes"
                placeholder="Select Type"
                optionLabel="label"
                optionValue="value"
                :class="{
                  'p-invalid': touched.seller_type && !form.seller_type,
                }"
                @blur="touched.seller_type = true"
              />
              <small
                v-if="touched.seller_type && !form.seller_type"
                class="p-error"
                >Seller type is required</small
              >
            </div>
            <div v-if="isAdmin" class="flex flex-column gap-1 col-12 md:col-6">
              <label for="acc_owners">Account Manager</label>
              <Dropdown
                input-id="acc_owners"
                v-model="form.group_account_manager"
                :options="accountOwners"
                option-label="name"
                option-value="id"
                placeholder="Select Owner"
                show-clear
              />
            </div>
          </div>
        </template>
      </Card>
      <Card class="mb-3">
        <template #title>
          <div class="text-xl">Contact Information</div>
        </template>
        <template #content>
          <div class="grid">
            <div class="col-12 md:col-6 flex flex-column gap-3 mb-3">
              <div class="flex flex-column gap-1 w-full">
                <label for="company_name">Company Name</label>
                <InputText
                  id="company_name"
                  v-model="form.company_name"
                  placeholder="Company Name"
                  :class="{
                    'p-invalid': touched.company_name && !form.company_name,
                  }"
                  @blur="touched.company_name = true"
                />
                <small
                  v-if="touched.company_name && !form.company_name"
                  class="p-error"
                  >Company name is required</small
                >
              </div>
              <div class="flex flex-column gap-1 w-full">
                <label for="company_address">Company Address</label>
                <InputText
                  id="company_address"
                  v-model="form.company_address"
                  placeholder="Company Address"
                />
              </div>
              <div class="flex flex-column gap-1 w-full">
                <label for="company_country_of_registration"
                  >Company Country</label
                >
                <InputText
                  id="company_country_of_registration"
                  v-model="form.company_country_of_registration"
                  placeholder="Company Country"
                />
              </div>
              <div class="flex flex-column gap-1 w-full">
                <label for="company_address_city">Company City</label>
                <InputText
                  id="company_address_city"
                  v-model="form.company_address_city"
                  placeholder="Company City"
                />
              </div>
              <div class="flex flex-column gap-1 w-full">
                <label for="company_address_zip_code">Company ZipCode</label>
                <InputText
                  id="company_address_zip_code"
                  v-model="form.company_address_zip_code"
                  placeholder="Company ZipCode"
                />
              </div>
            </div>
            <div class="col-12 md:col-6 flex flex-column gap-3 mb-3">
              <div class="flex flex-column gap-1 w-full">
                <label for="contact_person_name">Contact Person Name</label>
                <InputText
                  id="contact_person_name"
                  v-model="form.contact_person_name"
                  placeholder="Contact Person Name"
                />
              </div>
              <div class="flex flex-column gap-1 w-full">
                <label for="contact_person_title">Contact Person Title</label>
                <InputText
                  id="contact_person_title"
                  v-model="form.contact_person_title"
                  placeholder="Contact Person Title"
                />
              </div>
              <div class="flex flex-column gap-1 w-full">
                <label for="contact_person_email">Contact Person Email</label>
                <InputText
                  id="contact_person_email"
                  v-model="form.contact_person_email"
                  placeholder="Contact Person Email"
                />
              </div>
              <div class="flex flex-column gap-1 w-full">
                <label for="contact_person_phone_number"
                  >Contact Person Phone</label
                >
                <InputText
                  id="contact_person_phone_number"
                  v-model="form.contact_person_phone_number"
                  placeholder="Contact Person Phone"
                />
              </div>
              <div class="flex flex-column gap-1 w-full">
                <label for="contact_person_mobile_number"
                  >Contact Person Mobile</label
                >
                <InputText
                  id="contact_person_mobile_number"
                  v-model="form.contact_person_mobile_number"
                  placeholder="Contact Person Mobile"
                />
              </div>
            </div>
          </div>
        </template>
      </Card>
      <Card class="mb-3">
        <template #title>
          <div class="text-xl">Widget Metrics Permissions</div>
        </template>
        <template #content>
          <div class="grid">
            <div class="col-12 lg:col-6">
              <div class="permissions-block permissions-block--jamx">
                <Tag
                  icon="pi pi-microphone"
                  severity="info"
                  value="JamX"
                  class="capitalize border-round-3xl permissions-block-label"
                />
                <div class="flex align-items-end gap-3 flex-wrap">
                  <div class="flex align-items-center gap-2">
                    <Checkbox
                      inputId="show_gross_revenue"
                      v-model="form.show_gross_revenue"
                      :binary="true"
                    />
                    <label for="show_gross_revenue">Show Gross Revenue</label>
                  </div>
                  <div class="flex align-items-center gap-2">
                    <Checkbox
                      inputId="show_gross_profit"
                      v-model="form.show_gross_profit"
                      :binary="true"
                    />
                    <label for="show_gross_profit">Show Gross Profit</label>
                  </div>
                  <div class="flex align-items-center gap-2">
                    <Checkbox
                      inputId="can_add_sellers"
                      v-model="form.can_add_sellers"
                      :binary="true"
                    />
                    <label for="can_add_sellers">Can Add Sellers</label>
                  </div>
                  <div class="flex align-items-center gap-2">
                    <Checkbox
                      inputId="show_cpm"
                      v-model="form.show_cpm"
                      :binary="true"
                    />
                    <label for="show_cpm">Show CPM</label>
                  </div>
                  <div class="flex align-items-center gap-2">
                    <Checkbox
                      inputId="exclude_from_sellers_json"
                      v-model="form.exclude_from_sellers_json"
                      :binary="true"
                    />
                    <label for="exclude_from_sellers_json"
                      >Exclude from Sellers JSON</label
                    >
                  </div>

                  <template v-if="form.can_add_sellers">
                    <div class="flex flex-column gap-1">
                      <label for="default_contract_type"
                        >Default Contract Type:</label
                      >
                      <Dropdown
                        id="default_contract_type"
                        v-model="form.default_contract_type"
                        :options="contractTypes"
                        placeholder="Select Type"
                        optionLabel="label"
                        optionValue="value"
                        :class="{
                          'p-invalid': submitted && !form.default_contract_type,
                        }"
                        @change="
                          () => {
                            form.default_contract_value = 0;
                          }
                        "
                      />
                      <small
                        v-if="submitted && !form.default_contract_type"
                        class="p-error"
                        >Contract type is required</small
                      >
                    </div>
                    <div class="flex flex-column gap-1">
                      <template v-if="form.default_contract_type === 'cpm'">
                        <label
                          v-if="form.default_contract_type?.length"
                          for="default_contract_value"
                          >{{ contractValueLabel }}:</label
                        >
                        <InputText
                          id="default_contract_value"
                          v-model.number="form.default_contract_value"
                          :class="{
                            'p-invalid':
                              submitted && !form.default_contract_value,
                          }"
                        />
                        <small
                          v-if="submitted && !form.default_contract_value"
                          class="p-error"
                          >Contract value is required</small
                        >
                      </template>
                      <template
                        v-else-if="form.default_contract_type === 'revshare'"
                      >
                        <label
                          v-if="form.default_contract_type?.length"
                          for="default_contract_value"
                          >{{ contractValueLabel }}:</label
                        >
                        <input
                          id="default_contract-value"
                          type="range"
                          min="5"
                          max="75"
                          step="5"
                          v-model="form.default_contract_value"
                        />
                        <span style="text-align: left; width: 20%"
                          >{{ form.default_contract_value }}%</span
                        >
                        <small
                          v-if="submitted && !form.default_contract_value"
                          class="p-error"
                          >Contract value is required</small
                        >
                      </template>
                    </div>
                  </template>
                </div>
              </div>
            </div>
            <div class="col-12 lg:col-6">
              <div class="permissions-block permissions-block--jamz">
                <Tag
                  icon="pi pi-bolt"
                  severity="warning"
                  value="JamZ"
                  class="capitalize border-round-3xl permissions-block-label"
                />
                <div class="flex align-items-center gap-3 flex-wrap">
                  <div
                    v-for="metric in snippetMetricsList"
                    :key="metric.key"
                    class="flex align-items-center gap-2"
                  >
                    <Checkbox
                      :inputId="`snp_metric_${metric.key}`"
                      v-model="
                        form.snippet_widget_metrics_permissions[metric.key]
                          .visible
                      "
                      :binary="true"
                    />
                    <label :for="`snp_metric_${metric.key}`">{{
                      metric.label
                    }}</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </Card>
      <Card class="mb-3">
        <template #title>
          <div class="text-xl">Video Settings</div>
        </template>
        <template #content>
          <div class="grid">
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="video_monetization_enabled">Video Monetization</label>
              <SelectButton
                id="video_monetization_enabled"
                v-model="form.video_monetization_enabled"
                :options="BoolTypes"
                optionLabel="label"
                optionValue="value"
              />
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="connatix_reporting_enabled">Connatix Reporting</label>
              <SelectButton
                id="connatix_reporting_enabled"
                v-model="form.connatix_reporting_enabled"
                :options="BoolTypes"
                optionLabel="label"
                optionValue="value"
              />
            </div>
          </div>
        </template>
      </Card>

      <!-- Publisher Financial Details -->
      <Card class="mb-3">
        <template #title>
          <div class="text-xl">Publisher Financial Details</div>
        </template>
        <template #content>
          <div class="grid">
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="finance_contact_name">Finance Contact</label>
              <InputText
                id="finance_contact_name"
                v-model="form.finance_contact_name"
                placeholder="Finance Contact"
              />
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="accounting_dept_email">*Accounting Email</label>
              <InputText
                id="accounting_dept_email"
                v-model="form.accounting_dept_email"
                :class="{
                  'p-invalid':
                    touched.accounting_dept_email &&
                    (!form.accounting_dept_email || !accountingEmailValid),
                }"
                placeholder="Accounting Email"
                type="email"
                @blur="handleAccountingEmailBlur"
              />
              <small
                v-if="
                  touched.accounting_dept_email && !form.accounting_dept_email
                "
                class="p-error"
                >Accounting Email is required</small
              >
              <small
                v-else-if="
                  touched.accounting_dept_email &&
                  form.accounting_dept_email &&
                  !accountingEmailValid
                "
                class="p-error"
                >Invalid email format</small
              >
            </div>

            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="accounting_dept_phone_number"
                >Accounting Phone Number</label
              >
              <InputText
                id="accounting_dept_phone_number"
                v-model="form.accounting_dept_phone_number"
                placeholder="Accounting Phone Number"
              />
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="payment_term">Payment Term</label>
              <InputText
                id="payment_term"
                v-model="form.payment_term"
                placeholder="Payment Term"
              />
            </div>

            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="vendor_no">Vendor No.</label>
              <InputText
                id="vendor_no"
                v-model="form.vendor_no"
                placeholder="Vendor Number"
              />
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="bank_name">Bank (Name)</label>
              <InputText
                id="bank_name"
                v-model="form.bank_name"
                placeholder="Bank Name"
              />
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="bank_branch_code">Branch Code</label>
              <InputText
                id="bank_branch_code"
                v-model="form.bank_branch_code"
                placeholder="Branch Code"
              />
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="bank_account_number">Account Number</label>
              <InputText
                id="bank_account_number"
                v-model="form.bank_account_number"
                placeholder="Account Number"
              />
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="bank_iban">IBAN</label>
              <InputText
                id="bank_iban"
                v-model="form.bank_iban"
                placeholder="IBAN"
              />
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="bank_swift">SWIFT</label>
              <InputText
                id="bank_swift"
                v-model="form.bank_swift"
                placeholder="SWIFT"
              />
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="account_routing_type">Account Routing Type</label>
              <InputText
                id="account_routing_type"
                v-model="form.account_routing_type"
                placeholder="IBAN / SWIFT / ABA / ACH"
              />
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="account_routing_value">Account Routing Value</label>
              <InputText
                id="account_routing_value"
                v-model="form.account_routing_value"
                placeholder="Routing value"
              />
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="bank_account_postal_code"
                >Bank Account Postal Code</label
              >
              <InputText
                id="bank_account_postal_code"
                v-model="form.bank_account_postal_code"
                placeholder="Postal Code"
              />
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="bank_account_street_address"
                >Bank Account Street Address</label
              >
              <InputText
                id="bank_account_street_address"
                v-model="form.bank_account_street_address"
                placeholder="Street Address"
              />
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="bank_account_city">Bank Account City</label>
              <InputText
                id="bank_account_city"
                v-model="form.bank_account_city"
                placeholder="City"
              />
            </div>
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="bank_account_state">Bank Account State</label>
              <InputText
                id="bank_account_state"
                v-model="form.bank_account_state"
                placeholder="State / Region"
              />
            </div>
          </div>
        </template>
      </Card>
      <Card class="mb-3" v-if="publisherShows?.length">
        <template #title>
          <div class="text-xl">Promotion Campaigns</div>
        </template>
        <template #content>
          <div class="grid">
            <div class="flex flex-column gap-1 col-12 md:col-6">
              <label for="publisher_shows">Select Shows</label>
              <MultiSelect
                input-id="publisher_shows"
                :options="publisherShows"
                v-model="selectedPublisherShows"
                display="chip"
                placeholder="Select shows"
                :panelStyle="{ maxWidth: 'min(500px, 45vw)' }"
              >
                <template #option="slotProps">
                  {{
                    `${slotProps.option.show_title || "Show ID:"} [${
                      slotProps.option.show_id
                    }]`
                  }}
                </template>
              </MultiSelect>
            </div>
            <div
              v-if="selectedPublisherShows?.length"
              class="flex flex-column gap-1 col-12 md:col-6"
            >
              <label for="publisher_shows_start_promotion"
                >Create promotion campaigns</label
              >
              <Button
                label="Create Promotion Campaigns"
                @click="createPromotionCampaigns"
              />
            </div>
          </div>
        </template>
      </Card>
      <div class="w-full flex justify-content-end gap-2">
        <Button
          class="flex-1 sm:flex-initial"
          outlined
          label="Cancel"
          @click="router.go(-1)"
        />
        <Button class="flex-1 sm:flex-initial" type="submit" label="Submit" />
      </div>
    </form>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import InputText from "primevue/inputtext";
import Card from "primevue/card";
import Dropdown from "primevue/dropdown";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { useToast } from "primevue/usetoast";

import Button from "primevue/button";
import SelectButton from "primevue/selectbutton";
import Checkbox from "primevue/checkbox";
import Tag from "primevue/tag";
import axios from "axios";
import { API_BASE_URL } from "../consts";
import MultiSelect from "primevue/multiselect";

export default {
  components: {
    InputText,
    Button,
    Card,
    Dropdown,
    SelectButton,
    Checkbox,
    Tag,
    MultiSelect,
  },

  setup() {
    const store = useStore();
    const toast = useToast();

    const submitted = ref(false);

    const emailValid = ref(true);
    const domainValid = ref(true);
    const passwordValid = ref(true);
    const accountingEmailValid = ref(true);

    const touched = ref({
      name: false,
      email: false,
      password: false,
      company_name: false,
      company_domain: false,
      seller_type: false,
      accounting_dept_email: false,
      default_contract_type: false,
      default_contract_value: false,
    });

    const accountOwners = ref([]);

    const publisherShows = ref([]);

    const isAdmin = store.getters.isAdminLoggedIn;

    const token =
      localStorage.getItem("adminToken") || localStorage.getItem("token");

    const contractTypes = [
      { label: "Per Load", value: "cpm" },
      { label: "Revenue Share", value: "revshare" },
    ];

    const BoolTypes = [
      { label: "Disable", value: false },
      { label: "Allow", value: true },
    ];

    const sellerTypes = [
      { label: "Publisher", value: "PUBLISHER" },
      { label: "Intermediary", value: "INTERMEDIARY" },
      { label: "Both", value: "BOTH" },
    ];

    const snippetMetricsList = [
      { key: "content_performance", label: "Content Performance" },
      { key: "scroll_direction", label: "Scroll Direction (Left/Right)" },
      { key: "fab_opens", label: "Fab Opens" },
      { key: "share_count", label: "Shares" },
      { key: "cta_by_type", label: "CTA Clicks by Type" },
      { key: "end_screen_interactions", label: "End Screen Interactions" },
    ];

    const defaultSnippetMetricsPermissions = () =>
      Object.fromEntries(
        snippetMetricsList.map((metric) => [metric.key, { visible: true }])
      );

    const form = ref({
      name: "",
      email: "",
      password: "",
      company_name: "",
      company_address: "",
      company_country_of_registration: "",
      company_address_city: "",
      company_address_zip_code: "",
      contact_person_name: "",
      contact_person_title: "",
      contact_person_email: "",
      contact_person_phone_number: "",
      contact_person_mobile_number: "",
      finance_contact_name: "",
      vendor_no: "",
      accounting_dept_email: "",
      accounting_dept_phone_number: "",
      payment_term: "",
      default_contract_type: contractTypes[0].value,
      default_contract_value: 0,
      show_gross_profit: BoolTypes[0].value,
      show_gross_revenue: BoolTypes[0].value,
      can_add_sellers: BoolTypes[0].value,
      company_domain: "",
      seller_type: "PUBLISHER",
      display_id: "",
      group_account_manager: null,
      show_cpm: BoolTypes[0].value,
      exclude_from_sellers_json: BoolTypes[0].value,
      video_monetization_enabled: BoolTypes[0].value,
      connatix_reporting_enabled: BoolTypes[0].value,
      snippet_widget_metrics_permissions: defaultSnippetMetricsPermissions(),
      account_routing_type: "",
      account_routing_value: "",
      bank_name: "",
      bank_branch_code: "",
      bank_account_number: "",
      bank_iban: "",
      bank_swift: "",
      bank_account_postal_code: "",
      bank_account_street_address: "",
      bank_account_city: "",
      bank_account_state: "",
    });

    const selectedPublisherShows = ref([]);

    const router = useRouter();

    const groupId = router.currentRoute.value.params.groupId;

    const contractValueLabel = computed(() => {
      if (form.value.default_contract_type === "cpm") {
        return "CPM Rate";
      } else {
        return "Share Percent";
      }
    });

    const fetchAccountManagers = async () => {
      if (!isAdmin) return;
      store.commit("setLoading", true);
      try {
        const response = await axios.get(`${API_BASE_URL}/account-managers`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
          },
        });

        if (response.data.success) {
          accountOwners.value = response.data.data;
        }
      } catch (error) {
        console.error("Error fetching account managers:", error);
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Error fetching group account managers",
          life: 4000,
        });
      } finally {
        store.commit("setLoading", false);
      }
    };

    const fetchPublisherShows = async () => {
      if (!isAdmin || !groupId) return;
      try {
        store.commit("setLoading", true);
        const response = await axios.get(
          `${API_BASE_URL}/publisher-shows/${groupId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
            },
          }
        );

        if (response.data.success) {
          publisherShows.value = response.data.data;
        }
      } catch (error) {
        console.error("Error fetching publisher shows:", error);
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Error fetching group publisher shows",
          life: 4000,
        });
      } finally {
        store.commit("setLoading", false);
      }
    };

    const createPromotionCampaigns = async () => {
      try {
        store.commit("setLoading", true);
        const response = await axios.post(
          `${API_BASE_URL}/admin/promotion-campaign`,
          selectedPublisherShows.value,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
            },
          }
        );

        if (response.data.success) {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: "Promotion campaigns created successfully",
            life: 4000,
          });
        }
      } catch (error) {
        console.error("Error creating promotion campaigns:", error);
        toast.add({
          severity: "error",
          summary: "Error",
          detail: "Error creating promotion campaigns",
          life: 4000,
        });
      } finally {
        selectedPublisherShows.value = [];
        store.commit("setLoading", false);
      }
    };

    const fetchSellerGroup = async () => {
      try {
        store.commit("setLoading", true);
        if (!groupId) return;

        const response = await axios.get(
          API_BASE_URL + "/seller-group/" + groupId,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.success) {
          const sellerGroup = response.data.sellerGroup;

          form.value = sellerGroup;
          form.value.video_monetization_enabled =
            sellerGroup.video_monetization_enabled ?? false;
          form.value.connatix_reporting_enabled =
            sellerGroup.connatix_reporting_enabled ?? false;

          const rawSnippetPermissions =
            sellerGroup.snippet_widget_metrics_permissions;
          const parsedSnippetPermissions = rawSnippetPermissions
            ? typeof rawSnippetPermissions === "string"
              ? JSON.parse(rawSnippetPermissions)
              : rawSnippetPermissions
            : {};
          form.value.snippet_widget_metrics_permissions = {
            ...defaultSnippetMetricsPermissions(),
            ...parsedSnippetPermissions,
          };

          if (form.value.default_contract_type == "revshare")
            form.value.default_contract_value *= 100;
        }
      } catch (error) {
        console.error("Error fetching seller group:", error);
      } finally {
        store.commit("setLoading", false);
      }
    };

    const validateEmail = () => {
      if (!form.value.email) {
        emailValid.value = false;
        return;
      }
      const re =
        // eslint-disable-next-line no-useless-escape
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      emailValid.value = re.test(String(form.value.email).toLowerCase());
    };

    const validateAccountingEmail = () => {
      if (!form.value.accounting_dept_email) {
        accountingEmailValid.value = false;
        return;
      }
      const re =
        // eslint-disable-next-line no-useless-escape
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      accountingEmailValid.value = re.test(
        String(form.value.accounting_dept_email).toLowerCase()
      );
    };

    const handleEmailBlur = () => {
      touched.value.email = true;
      validateEmail();
    };

    const handleAccountingEmailBlur = () => {
      touched.value.accounting_dept_email = true;
      validateAccountingEmail();
    };

    const handlePasswordBlur = () => {
      touched.value.password = true;
      validatePassword();
    };

    const handleDomainBlur = () => {
      touched.value.company_domain = true;
      validateDomain();
    };

    const cleanDomainInput = (domain) =>
      String(domain || "")
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, "")
        .replace(/^www\./, "")
        .replace(/\/.*$/, "");

    const validateDomain = () => {
      // eslint-disable-next-line no-useless-escape
      const re =
        /^(?=.{1,253}$)(?!-)(?:[A-Za-z0-9-]{1,63}(?<!-)\.)+[A-Za-z]{2,63}$/;
      const cleanedDomain = cleanDomainInput(form.value.company_domain);
      form.value.company_domain = cleanedDomain;
      domainValid.value = re.test(cleanedDomain);
    };

    const validatePassword = () => {
      passwordValid.value = !(
        !form.value.password ||
        form.value.password.length < 8 ||
        !(
          /[a-z]/.test(form.value.password) && /[A-Z]/.test(form.value.password)
        )
      );
    };

    const submitForm = async () => {
      submitted.value = true;

      // Mark all required fields as touched on submit
      touched.value.name = true;
      touched.value.email = true;
      touched.value.company_name = true;
      touched.value.company_domain = true;
      touched.value.seller_type = true;
      touched.value.accounting_dept_email = true;

      if (!groupId) {
        touched.value.password = true;
      }

      if (form.value.can_add_sellers) {
        touched.value.default_contract_type = true;
        touched.value.default_contract_value = true;
      }

      // Validate all fields
      validateEmail();
      validateAccountingEmail();
      validateDomain();

      if (!groupId) {
        validatePassword();
      } else {
        passwordValid.value = true;
        form.value.groupId = groupId;
      }

      // Check all required fields
      if (
        !form.value.name ||
        !form.value.email ||
        !form.value.company_name ||
        !form.value.company_domain ||
        !form.value.seller_type ||
        !form.value.accounting_dept_email ||
        !emailValid.value ||
        !accountingEmailValid.value ||
        !passwordValid.value ||
        !domainValid.value
      ) {
        toast.add({
          severity: "warn",
          summary: "Validation Error",
          detail: "Please fill in all required fields correctly",
          life: 4000,
        });
        return;
      }

      // Check contract fields if can_add_sellers is enabled
      if (
        form.value.can_add_sellers &&
        (!form.value.default_contract_type?.length ||
          form.value.default_contract_value == 0 ||
          !form.value.default_contract_value)
      ) {
        toast.add({
          severity: "warn",
          summary: "Validation Error",
          detail:
            "Contract type and value are required when sellers can be added",
          life: 4000,
        });
        return;
      }
      // All validation passed, submit the form
      try {
        store.commit("setLoading", true);

        const payload = { ...form.value };
        if (groupId) payload.groupId = groupId;

        const response = await axios.post(
          `${API_BASE_URL}/seller-group`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data?.success) {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: `Group ${groupId ? "updated" : "added"} successfully!`,
            life: 4000,
          });
          router.go(-1);
        } else {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: response.data?.message || "Something went wrong...",
            life: 4000,
          });
        }
      } catch (error) {
        toast.add({
          severity: "error",
          summary: "Error",
          detail: error.response?.data?.message || "Something went wrong...",
          life: 4000,
        });
        console.error(error);
      } finally {
        store.commit("setLoading", false);
      }
    };
    onMounted(() => {
      fetchSellerGroup();
      fetchAccountManagers();
      fetchPublisherShows();
    });

    return {
      form,
      router,
      submitForm,
      fetchSellerGroup,
      submitted,
      emailValid,
      accountingEmailValid,
      validatePassword,
      passwordValid,
      domainValid,
      contractValueLabel,
      contractTypes,
      BoolTypes,
      sellerTypes,
      snippetMetricsList,
      accountOwners,
      isAdmin,
      groupId,
      publisherShows,
      selectedPublisherShows,
      createPromotionCampaigns,
      touched,
      handleEmailBlur,
      handleAccountingEmailBlur,
      handlePasswordBlur,
      handleDomainBlur,
    };
  },
};
</script>

<style scoped>
.permissions-block {
  height: 100%;
  padding: 1rem 1.25rem;
  border-radius: var(--border-radius);
  border-left: 4px solid var(--surface-300);
  background: var(--surface-50);
}

.permissions-block-label {
  margin-bottom: 0.75rem;
}

.permissions-block--jamx {
  border-left-color: var(--blue-500);
}

.permissions-block--jamz {
  border-left-color: var(--orange-500);
}
</style>
